import { FC, useMemo } from "react";

import { QuestionResults, Section } from "@/types/resources";
import MaxDiffReport, { MaxDiffReportData } from "./MaxDiffReport";
import { itemsFromQuestion } from "./_itemsFromQuestion";

interface MaxDiffSectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/**
 * MaxDiff sections are templated as one "best/worst" matrix
 * question with two lines: line 0 = "best", line 1 = "worst".
 * The item pool is the matrix question's columns (`options`),
 * and the stored choice indices point into that column list.
 *
 * Preference share per item = (#best − #worst) renormalised so
 * the full set sums to 100%. Items below the cohort average pull
 * negative on the chart; the centred bar chart is the standard
 * MaxDiff visualisation.
 *
 * If no answers were captured we fall back to a flat distribution
 * (100/N each) so the chart still renders without items dog-piling
 * at zero.
 */
function cookMaxDiffData(
  data: QuestionResults[] | undefined
): MaxDiffReportData {
  const matrixQuestion = (data ?? []).find(
    (r) =>
      r?.question?.type === "single_select_matrix" ||
      r?.question?.type === "multi_select_matrix"
  );
  const items = itemsFromQuestion(matrixQuestion?.question).map((item) => ({
    id: item.id,
    name: item.name,
  }));

  const wins = items.map(() => 0);
  const losses = items.map(() => 0);
  /* Unique persona IDs across every cell of every MaxDiff task — a
     single respondent contributes many answers (one per rotation
     task), so the answer count itself would massively overstate the
     sample. Deduping on `persona_id` gives the true respondent N. */
  const personaIds = new Set<string>();

  const bumpInRange = (idx: number | null | undefined, bucket: number[]) => {
    if (
      typeof idx === "number" &&
      Number.isFinite(idx) &&
      idx >= 0 &&
      idx < bucket.length
    ) {
      bucket[idx] += 1;
    }
  };

  (data ?? []).forEach((r) => {
    const qType = r?.question?.type;
    if (qType !== "single_select_matrix" && qType !== "multi_select_matrix") {
      return;
    }
    /* Convention: `lines[0]` = "best", `lines[1]` = "worst". When
       only one line exists we treat every selection as a "best"
       so single-select MaxDiff variants degrade gracefully. */
    const isBestWorst = (r.question.lines?.length ?? 0) >= 2;

    (r.groups ?? []).forEach((g) => {
      (g.answers ?? []).forEach((a) => {
        personaIds.add(a.persona.id);
        if (a.answer.type === "single_select_matrix") {
          /* `choices[i]` is the column index picked for line `i`. */
          const choices = a.answer.choices ?? [];
          if (isBestWorst) {
            bumpInRange(choices[0], wins);
            bumpInRange(choices[1], losses);
          } else {
            choices.forEach((c) => bumpInRange(c, wins));
          }
        } else if (a.answer.type === "multi_select_matrix") {
          /* `choices[i]` is the array of column indices for line `i`. */
          const choices = a.answer.choices ?? [];
          if (isBestWorst) {
            (choices[0] ?? []).forEach((c) => bumpInRange(c, wins));
            (choices[1] ?? []).forEach((c) => bumpInRange(c, losses));
          } else {
            choices.forEach((row) =>
              (row ?? []).forEach((c) => bumpInRange(c, wins))
            );
          }
        }
      });
    });
  });

  /* Net preference, shifted to be non-negative so we can renormalise
     to a share. Items picked as "worst" more often than "best" still
     end up at zero, never below. */
  const net = wins.map((w, i) => w - (losses[i] ?? 0));
  const minNet = net.length > 0 ? Math.min(0, ...net) : 0;
  const adjusted = net.map((n) => n - minNet);
  const total = adjusted.reduce((s, v) => s + v, 0);

  let shares: number[];
  if (total > 0) {
    shares = adjusted.map((v) => (v / total) * 100);
  } else if (items.length > 0) {
    shares = items.map(() => 100 / items.length);
  } else {
    shares = [];
  }

  return { items, sampleSize: personaIds.size, shares };
}

/* ── Component ──────────────────────────────────────────── */

const MaxDiffSectionResults: FC<MaxDiffSectionResultsProps> = ({ data }) => {
  const reportData: MaxDiffReportData = useMemo(
    () => cookMaxDiffData(data),
    [data]
  );
  return <MaxDiffReport data={reportData} />;
};

export default MaxDiffSectionResults;
